import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { ChangeCredential, SessionUser, StaffLogin, StudentLogin } from '@padhai/shared';
import { apiGet, apiPost, ApiError } from './api.js';

/**
 * Who is signed in, for the whole app.
 *
 * The session itself is an httpOnly cookie the browser sends automatically;
 * this only mirrors what the server says about it, and re-asks on load.
 */

interface AuthValue {
  user: SessionUser | null;
  /** True until the first /auth/me answer, so guards do not flash the login page. */
  loading: boolean;
  /**
   * The session could not be checked: the server answered with something
   * other than 401, or did not answer at all. Not the same as signed out — a
   * student with a valid cookie on a flaky connection must not be sent to the
   * sign-in page, so guards show a retry instead.
   */
  unreachable: boolean;
  /** True while a retry of the session check is on its way. */
  retrying: boolean;
  /** Asks the server about the session again. Also runs on the `online` event. */
  retry(): void;
  signInStudent(input: StudentLogin): Promise<void>;
  signInStaff(input: StaffLogin): Promise<void>;
  signOut(): Promise<void>;
  /**
   * Changes the signed-in person's own PIN or password. The server replaces
   * the session and returns the profile, which clears `mustChangeCredential`.
   */
  changeCredential(input: ChangeCredential): Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [unreachable, setUnreachable] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    apiGet<{ user: SessionUser }>('/api/v1/auth/me', controller.signal)
      .then(({ user: loaded }) => {
        setUser(loaded);
        setUnreachable(false);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        // 401 is the normal answer for a visitor with no cookie, not a failure.
        // Anything else — a 500, a dropped connection — says nothing about the
        // session, so it is not treated as signed out.
        if (error instanceof ApiError && error.status === 401) {
          setUser(null);
          setUnreachable(false);
        } else {
          setUnreachable(true);
        }
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
        setRetrying(false);
      });
    return () => controller.abort();
  }, [attempt]);

  const retry = useCallback(() => {
    setRetrying(true);
    setAttempt((count) => count + 1);
  }, []);

  // Coming back online is the most likely moment the check will succeed.
  useEffect(() => {
    if (!unreachable) return;
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, [unreachable, retry]);

  const signInStudent = useCallback(async (input: StudentLogin) => {
    const { user: signedIn } = await apiPost<{ user: SessionUser }>(
      '/api/v1/auth/student/login',
      input,
    );
    setUser(signedIn);
    setUnreachable(false);
  }, []);

  const signInStaff = useCallback(async (input: StaffLogin) => {
    const { user: signedIn } = await apiPost<{ user: SessionUser }>(
      '/api/v1/auth/staff/login',
      input,
    );
    setUser(signedIn);
    setUnreachable(false);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await apiPost('/api/v1/auth/logout');
    } finally {
      // Whatever the server said, this browser is done with the session.
      setUser(null);
    }
  }, []);

  const changeCredential = useCallback(async (input: ChangeCredential) => {
    const { user: updated } = await apiPost<{ user: SessionUser }>(
      '/api/v1/auth/credential',
      input,
    );
    setUser(updated);
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      loading,
      unreachable,
      retrying,
      retry,
      signInStudent,
      signInStaff,
      signOut,
      changeCredential,
    }),
    [
      user,
      loading,
      unreachable,
      retrying,
      retry,
      signInStudent,
      signInStaff,
      signOut,
      changeCredential,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside an AuthProvider.');
  return value;
}
